import Component from "../component";
import Urls from "../../configuration/urls.config";
import { loadScript } from "../../utils/load_script";
import { replace, getFeaturePosition } from "../../utils/utils";
import { getLocale, getLocaleLang } from "../../helpers/locale";
import { getConfig } from "configuration/config";
import { GeoJSONFeature } from "Main";

export interface IMapComponent {
    woosmapPublicKey: string;
    mapOptions: woosmap.map.MapOptions;
    indoorRendererOptions: woosmap.map.IndoorRendererOptions;
    indoorWidgetOptions: woosmap.map.IndoorWidgetOptions;
    query?: string;
    padding?: woosmap.map.Padding;
    zoom?: number;
    filter?: string
    libraries: string;
}

export enum MapComponentEvents {
    MAP_READY = "map_ready",
    MAP_IDLE = "idle",
    INDOOR_VENUE_LOADED = 'indoor_venue_loaded',
    INDOOR_WIDGET_ANALYTICS = 'indoor_widget_analytics',
    INDOOR_POI_ON_MAP_CLICK = 'indoor_feature_selected',
    INDOOR_HIGHLIGHT_STEP = 'indoor_highlight_step',
    INDOOR_STOP_NAVIGATION = 'indoor_stop_navigation',
    INDOOR_LEVEL_CHANGED = 'indoor_level_changed',
    FAVORITE_MARKER_CLICKED = 'fav_marker_clicked'
}

export default class MapComponent extends Component<IMapComponent> {
    public map!: woosmap.map.Map;
    public indoorMap!: woosmap.map.IndoorWidget;
    public favMarkers!: woosmap.map.Marker[];
    public currentLevel!: number;
    public userFavorites!: woosmap.map.GeoJSONFeature[];

    init(): void {
        this.$element = document.createElement("div");
        this.$element.id = 'map';
        this.$target.appendChild(this.$element);
    }
    render(): void {
        if (this.state && this.$element) {
            loadScript({
                url: Urls.mapJS,
                params: { key: this.state.woosmapPublicKey, libraries: this.state.libraries, language: getLocaleLang().toLowerCase() },
                override: true,
                callback: () => { this.initMapView() }
            })
                .then(() => {
                    // this.initMapView();
                    console.log('Scripts loaded...');
                })
                .catch((error) => {
                    console.error(
                        replace(getLocale().errors.loadingLibrary, { 'library': "Woosmap Map JS SDK script" }),
                        error
                    );
                });
        }
    }

    initMapView(): void {
        this.map = new woosmap.map.Map(
            'map',
            Object.assign({}, this.state.mapOptions)
        )

        this.emit(MapComponentEvents.MAP_READY);
        this.map.addListener(MapComponentEvents.MAP_IDLE, () => {
            this.emit(MapComponentEvents.MAP_IDLE);
        })
        this.map.addListener('rightclick', (e) => {
            console.log(e);
        })
    }
    initIndoorView(): void {
        const widgetOptions = this.state.query && this.state.query !== `thematic:=${getConfig().map.indoorRendererOptions.theme}` ? { ...this.state.indoorWidgetOptions, baseFilter: this.state.query } : this.state.indoorWidgetOptions;
        const rendererOptions = this.state.query && this.state.query !== `thematic:=${getConfig().map.indoorRendererOptions.theme}` ? { ...this.state.indoorRendererOptions, baseFilter: this.state.query } : this.state.indoorRendererOptions;
        this.indoorMap = new woosmap.map.IndoorWidget(widgetOptions, rendererOptions);
        this.indoorMap.setMap(this.map);
        this.indoorMap.addListener(
            MapComponentEvents.INDOOR_VENUE_LOADED,
            (venue: woosmap.map.Venue) => {
                this.currentLevel = 0;
                this.emit(MapComponentEvents.INDOOR_VENUE_LOADED, venue);
            }
        );
        this.indoorMap.addListener(
            MapComponentEvents.INDOOR_WIDGET_ANALYTICS,
            (event: any) => {
                this.emit(MapComponentEvents.INDOOR_WIDGET_ANALYTICS, event);
            }
        );
        this.indoorMap.addListener(
            MapComponentEvents.INDOOR_POI_ON_MAP_CLICK,
            (event: any) => {
                this.emit(MapComponentEvents.INDOOR_POI_ON_MAP_CLICK, event);
            }
        );
        this.indoorMap.addListener(
            MapComponentEvents.INDOOR_HIGHLIGHT_STEP,
            (event: any) => {
                this.emit(MapComponentEvents.INDOOR_HIGHLIGHT_STEP, event);
            }
        );
        this.indoorMap.addListener(
            MapComponentEvents.INDOOR_STOP_NAVIGATION,
            (event: any) => {
                this.emit(MapComponentEvents.INDOOR_STOP_NAVIGATION, event);
            }
        );
        this.indoorMap.addListener(
            MapComponentEvents.INDOOR_LEVEL_CHANGED,
            (event: any) => {
                this.currentLevel = event;
                if (this.userFavorites) {
                    this.plotUserFavoritesOnMap(this.userFavorites);
                }

            }
        );

    }
    setFilter(filter: string): void {
        const filterToApply = filter !== undefined ? (`thematic:=${filter} OR ${filter}:=true`) : ``;
        if (filter !== getConfig().map.themes["default"] && filter !== getConfig().map.themes["favorites"]) {
            this.indoorMap.getRenderer().setBaseFilter(filterToApply);
            this.indoorMap.setBaseFilter(filterToApply);
        }
        else {
            this.indoorMap.getRenderer().setBaseFilter("");
            this.indoorMap.setBaseFilter("");
        }
    }
    setOrigin(data: any): void {
        this.indoorMap.setUserLocation(data.lat, data.lng, data.level || 0, 0, true);
    }
    setTheme(theme: string): void {
        const themeToApply = theme !== undefined ? theme : getConfig().map.themes["default"];
        if (this.indoorMap) {
            this.indoorMap.setTheme(themeToApply);
        }

    }
    setBounds(bounds: woosmap.map.LatLngBounds) {
        let padding = {
            top: 0, left: 150, right: 150, bottom: 0
        };
        if (this.$element.clientWidth > 768) {
            padding = {
                top: 350, left: 350, right: 350, bottom: 350
            };
        }
        this.map.fitBounds(bounds, padding, true);
    }
    unselectPOI(): void {
        this.indoorMap.unselectFeature();
    }
    turnOffNavigation(): void {
        this.indoorMap.setNavigationMode(false);
    }
    getDirections(): void {
        return this.indoorMap.getRenderer()._directionsRenderer.directions;
    }
    showRouteForAllLevels(): void {
        if (this.indoorMap) {
            const mapStyle = this.map._style;
            if (mapStyle) {
                mapStyle.addLayer(
                    {
                        id: 'indoor-directions-all-levels',
                        type: 'line',
                        source: 'indoor-directions',
                        filter: ['all', ['==', ['get', 'enabled'], false]],
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                        paint: {
                            'line-color': '#808AC7',
                            'line-opacity': 0.8,
                            'line-width': 6,
                        },
                    },
                    'indoor-directions_layers'
                );
            }

        }

    }
    showRouteForSponsor(origin: woosmap.map.LatLngLiteral, destination: string): void {
        const originPoint = `${origin.lat},${origin.lng},0`;
        const indoorService = new woosmap.map.IndoorService();
        const cbSearch = (result: woosmap.map.GeoJSONFeatureCollection) => {
            if (result && result.features && result.features.length > 0) {
                const feature = result.features[0];
                this.indoorMap.showItinerary(originPoint, feature);
            }
        };
        // TO DO REFACTOR : Awaiting data from client to get sponsored logos and defining final structure.
        indoorService.search(getConfig().map.indoorRendererOptions.venue || `sdm_${getConfig().map.eventYear}_${getConfig().map.env}`, '', cbSearch, destination);
    }
    plotUserFavoritesOnMap(favorites: GeoJSONFeature[]) {
        console.log("Inside plotUserFavoritesOnMap....", favorites);
        this.removeMarkersForFavs();
        this.userFavorites = favorites;
        this.favMarkers = [];
        for (const fav of favorites) {
            if (parseInt(fav.properties.level) === this.currentLevel) {
                const marker = new woosmap.map.Marker({
                    position: getFeaturePosition(fav),
                    icon: {
                        url: getConfig().urls.favMarkerUrl,
                        scaledSize: {
                            height: 28,
                            width: 28,
                        },
                    },
                });
                marker.addListener("click", () => {
                    this.indoorMap.highlightFeature(fav.id, false);
                    this.emit(MapComponentEvents.FAVORITE_MARKER_CLICKED, fav);
                })
                this.favMarkers.push(marker);
                marker.setMap(this.map);
            }
        }
    }
    removeMarkersForFavs(): void {
        if (this.favMarkers) {
            this.favMarkers.forEach(marker => {
                marker.setMap(null);
            });
            this.favMarkers = [];
            this.userFavorites = [];
        }
    }
    destroyMap(): void {
        if(this.indoorMap) {
            this.indoorMap.getRenderer().setMap(null);
            this.indoorMap = null;
            this.map = new woosmap.map.Map(
                'map',
                Object.assign({}, this.state.mapOptions)
            )
            this.initIndoorView();
        }
       
    }
}
